import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SUBSCRIPTION_EXPIRED_EVENT,
  getAuthToken,
} from "../services/authService";
import {
  MySubscription,
  getMySubscription,
} from "../services/subscriptionService";
import { useUser } from "./UserContext";

type SubscriptionContextType = {
  /** null until loaded, and for anyone not signed in to an organization. */
  subscription: MySubscription | null;
  refreshSubscription: () => Promise<void>;
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export const useSubscription = (): SubscriptionContextType => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return ctx;
};

export const SUBSCRIPTION_PATH = "/subscription";

// Pages that work without a live subscription: the packages screen itself,
// the public/auth pages, and the superadmin panel (its own session).
const OPEN_PREFIXES = [
  SUBSCRIPTION_PATH,
  "/login",
  "/signup",
  "/forgot-password",
  "/invite",
  "/match",
  "/superadmin",
];

const isOpenPath = (pathname: string) =>
  OPEN_PREFIXES.some((prefix) => pathname.startsWith(prefix));

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [subscription, setSubscription] = useState<MySubscription | null>(null);

  const refreshSubscription = useCallback(async () => {
    if (!getAuthToken()) {
      setSubscription(null);
      return;
    }
    try {
      setSubscription(await getMySubscription());
    } catch {
      // Keep the last known state; the backend still enforces the lock.
    }
  }, []);

  // Once per signed-in organization (login, reload, or switching accounts).
  const organizationId = (user as any)?.organizationId as string | undefined;
  useEffect(() => {
    if (organizationId) refreshSubscription();
    else setSubscription(null);
  }, [organizationId, refreshSubscription]);

  // The end date can pass, or the superadmin can end it, mid-session: the
  // first refused request re-reads the status and moves to the lock screen.
  useEffect(() => {
    const handler = () => {
      refreshSubscription();
      if (!isOpenPath(window.location.pathname)) {
        navigate(SUBSCRIPTION_PATH, { replace: true });
      }
    };
    window.addEventListener(SUBSCRIPTION_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(SUBSCRIPTION_EXPIRED_EVENT, handler);
  }, [navigate, refreshSubscription]);

  // Known expired at load: don't render a dashboard whose every call fails.
  useEffect(() => {
    if (subscription?.status === "EXPIRED" && !isOpenPath(location.pathname)) {
      navigate(SUBSCRIPTION_PATH, { replace: true });
    }
  }, [subscription?.status, location.pathname, navigate]);

  return (
    <SubscriptionContext.Provider value={{ subscription, refreshSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
